import { LOCK_TYPE } from '../types';

import type { VisualizationAxis, VisualizationMeasurement } from './types';

export const DISPLACEMENT_MEASUREMENTS = {
    head_linear_displacement: {
        label: 'Head',
        type: 'measurement',
        axes: ['head_linear_displacement_thrust', 'head_linear_displacement_sway', 'head_linear_displacement_lift'],
    },
    upper_torso_linear_displacement: {
        label: 'Shoulders Mid',
        type: 'measurement',
        axes: [
            'upper_torso_linear_displacement_thrust',
            'upper_torso_linear_displacement_sway',
            'upper_torso_linear_displacement_lift',
        ],
    },
    left_wrist_linear_displacement: {
        label: 'Lead Wrist',
        type: 'measurement',
        axes: [
            'left_wrist_linear_displacement_thrust',
            'left_wrist_linear_displacement_sway',
            'left_wrist_linear_displacement_lift',
        ],
    },
    right_wrist_linear_displacement: {
        label: 'Trail Wrist',
        type: 'measurement',
        axes: [
            'right_wrist_linear_displacement_thrust',
            'right_wrist_linear_displacement_sway',
            'right_wrist_linear_displacement_lift',
        ],
    },
    left_ankle_linear_displacement: {
        label: 'Lead ankle',
        type: 'measurement',
        axes: [
            'left_ankle_linear_displacement_thrust',
            'left_ankle_linear_displacement_sway',
            'left_ankle_linear_displacement_lift',
        ],
    },
    right_ankle_linear_displacement: {
        label: 'Trail ankle',
        type: 'measurement',
        axes: [
            'right_ankle_linear_displacement_thrust',
            'right_ankle_linear_displacement_sway',
            'right_ankle_linear_displacement_lift',
        ],
    },
    left_knee_linear_displacement: {
        label: 'Lead knee',
        type: 'measurement',
        axes: [
            'left_knee_linear_displacement_thrust',
            'left_knee_linear_displacement_sway',
            'left_knee_linear_displacement_lift',
        ],
    },
    right_knee_linear_displacement: {
        label: 'Trail knee',
        type: 'measurement',
        axes: [
            'right_knee_linear_displacement_thrust',
            'right_knee_linear_displacement_sway',
            'right_knee_linear_displacement_lift',
        ],
    },
    left_elbow_linear_displacement: {
        label: 'Lead elbow',
        type: 'measurement',
        axes: [
            'left_elbow_linear_displacement_thrust',
            'left_elbow_linear_displacement_sway',
            'left_elbow_linear_displacement_lift',
        ],
    },
    right_elbow_linear_displacement: {
        label: 'Trail elbow',
        type: 'measurement',
        axes: [
            'right_elbow_linear_displacement_thrust',
            'right_elbow_linear_displacement_sway',
            'right_elbow_linear_displacement_lift',
        ],
    },
    left_shoulder_linear_displacement: {
        label: 'Lead shoulder',
        type: 'measurement',
        axes: [
            'left_shoulder_linear_displacement_thrust',
            'left_shoulder_linear_displacement_sway',
            'left_shoulder_linear_displacement_lift',
        ],
    },
    right_shoulder_linear_displacement: {
        label: 'Trail shoulder',
        type: 'measurement',
        axes: [
            'right_shoulder_linear_displacement_thrust',
            'right_shoulder_linear_displacement_sway',
            'right_shoulder_linear_displacement_lift',
        ],
    },
    left_hip_linear_displacement: {
        label: 'Lead hip',
        type: 'measurement',
        axes: [
            'left_hip_linear_displacement_thrust',
            'left_hip_linear_displacement_sway',
            'left_hip_linear_displacement_lift',
        ],
    },
    right_hip_linear_displacement: {
        label: 'Trail hip',
        type: 'measurement',
        axes: [
            'right_hip_linear_displacement_thrust',
            'right_hip_linear_displacement_sway',
            'right_hip_linear_displacement_lift',
        ],
    },
    pelvis_linear_displacement: {
        label: 'Pelvis',
        type: 'measurement',
        axes: [
            'pelvis_linear_displacement_thrust',
            'pelvis_linear_displacement_sway',
            'pelvis_linear_displacement_lift',
        ],
    },
} as const satisfies Record<string, VisualizationMeasurement>;

export const DISPLACEMENT_MEASUREMENTS_PARENT_MAP = Object.fromEntries(
    Object.entries(DISPLACEMENT_MEASUREMENTS).flatMap(([measurementId, measurement]) =>
        measurement.axes.map((axisId) => [axisId, measurementId] as [string, string]),
    ),
);

export const DISPLACEMENT_AXES: Record<string, VisualizationAxis> = {
    head_linear_displacement_thrust: {
        id: 'head_linear_displacement_thrust',
        measurementName: 'head_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['head'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    head_linear_displacement_sway: {
        id: 'head_linear_displacement_sway',
        measurementName: 'head_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['head'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    head_linear_displacement_lift: {
        id: 'head_linear_displacement_lift',
        measurementName: 'head_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['head'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    upper_torso_linear_displacement_thrust: {
        id: 'upper_torso_linear_displacement_thrust',
        measurementName: 'upper_torso_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['upper_torso'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    upper_torso_linear_displacement_sway: {
        id: 'upper_torso_linear_displacement_sway',
        measurementName: 'upper_torso_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['upper_torso'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    upper_torso_linear_displacement_lift: {
        id: 'upper_torso_linear_displacement_lift',
        measurementName: 'upper_torso_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['upper_torso'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_wrist_linear_displacement_thrust: {
        id: 'left_wrist_linear_displacement_thrust',
        measurementName: 'left_wrist_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['left_wrist'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    left_wrist_linear_displacement_sway: {
        id: 'left_wrist_linear_displacement_sway',
        measurementName: 'left_wrist_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['left_wrist'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_wrist_linear_displacement_lift: {
        id: 'left_wrist_linear_displacement_lift',
        measurementName: 'left_wrist_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['left_wrist'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    right_wrist_linear_displacement_thrust: {
        id: 'right_wrist_linear_displacement_thrust',
        measurementName: 'right_wrist_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['right_wrist'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    right_wrist_linear_displacement_sway: {
        id: 'right_wrist_linear_displacement_sway',
        measurementName: 'right_wrist_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['right_wrist'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    right_wrist_linear_displacement_lift: {
        id: 'right_wrist_linear_displacement_lift',
        measurementName: 'right_wrist_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['right_wrist'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    left_ankle_linear_displacement_thrust: {
        id: 'left_ankle_linear_displacement_thrust',
        measurementName: 'left_ankle_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['left_ankle'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    left_ankle_linear_displacement_sway: {
        id: 'left_ankle_linear_displacement_sway',
        measurementName: 'left_ankle_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['left_ankle'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_ankle_linear_displacement_lift: {
        id: 'left_ankle_linear_displacement_lift',
        measurementName: 'left_ankle_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['left_ankle'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    right_ankle_linear_displacement_thrust: {
        id: 'right_ankle_linear_displacement_thrust',
        measurementName: 'right_ankle_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['right_ankle'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    right_ankle_linear_displacement_sway: {
        id: 'right_ankle_linear_displacement_sway',
        measurementName: 'right_ankle_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['right_ankle'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    right_ankle_linear_displacement_lift: {
        id: 'right_ankle_linear_displacement_lift',
        measurementName: 'right_ankle_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['right_ankle'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    left_knee_linear_displacement_thrust: {
        id: 'left_knee_linear_displacement_thrust',
        measurementName: 'left_knee_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['left_knee'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    left_knee_linear_displacement_sway: {
        id: 'left_knee_linear_displacement_sway',
        measurementName: 'left_knee_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['left_knee'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_knee_linear_displacement_lift: {
        id: 'left_knee_linear_displacement_lift',
        measurementName: 'left_knee_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['left_knee'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    right_knee_linear_displacement_thrust: {
        id: 'right_knee_linear_displacement_thrust',
        measurementName: 'right_knee_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['right_knee'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    right_knee_linear_displacement_sway: {
        id: 'right_knee_linear_displacement_sway',
        measurementName: 'right_knee_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['right_knee'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    right_knee_linear_displacement_lift: {
        id: 'right_knee_linear_displacement_lift',
        measurementName: 'right_knee_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['right_knee'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    left_elbow_linear_displacement_thrust: {
        id: 'left_elbow_linear_displacement_thrust',
        measurementName: 'left_elbow_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['left_elbow'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    left_elbow_linear_displacement_sway: {
        id: 'left_elbow_linear_displacement_sway',
        measurementName: 'left_elbow_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['left_elbow'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_elbow_linear_displacement_lift: {
        id: 'left_elbow_linear_displacement_lift',
        measurementName: 'left_elbow_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['left_elbow'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    right_elbow_linear_displacement_thrust: {
        id: 'right_elbow_linear_displacement_thrust',
        measurementName: 'right_elbow_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['right_elbow'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    right_elbow_linear_displacement_sway: {
        id: 'right_elbow_linear_displacement_sway',
        measurementName: 'right_elbow_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['right_elbow'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    right_elbow_linear_displacement_lift: {
        id: 'right_elbow_linear_displacement_lift',
        measurementName: 'right_elbow_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['right_elbow'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    left_shoulder_linear_displacement_thrust: {
        id: 'left_shoulder_linear_displacement_thrust',
        measurementName: 'left_shoulder_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['left_shoulder'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    left_shoulder_linear_displacement_sway: {
        id: 'left_shoulder_linear_displacement_sway',
        measurementName: 'left_shoulder_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['left_shoulder'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_shoulder_linear_displacement_lift: {
        id: 'left_shoulder_linear_displacement_lift',
        measurementName: 'left_shoulder_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['left_shoulder'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    right_shoulder_linear_displacement_thrust: {
        id: 'right_shoulder_linear_displacement_thrust',
        measurementName: 'right_shoulder_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['right_shoulder'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    right_shoulder_linear_displacement_sway: {
        id: 'right_shoulder_linear_displacement_sway',
        measurementName: 'right_shoulder_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['right_shoulder'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    right_shoulder_linear_displacement_lift: {
        id: 'right_shoulder_linear_displacement_lift',
        measurementName: 'right_shoulder_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['right_shoulder'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    left_hip_linear_displacement_thrust: {
        id: 'left_hip_linear_displacement_thrust',
        measurementName: 'left_hip_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['left_hip'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    left_hip_linear_displacement_sway: {
        id: 'left_hip_linear_displacement_sway',
        measurementName: 'left_hip_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['left_hip'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    left_hip_linear_displacement_lift: {
        id: 'left_hip_linear_displacement_lift',
        measurementName: 'left_hip_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['left_hip'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: Math.PI / 2, polar: Math.PI / 2 },
    },
    right_hip_linear_displacement_thrust: {
        id: 'right_hip_linear_displacement_thrust',
        measurementName: 'right_hip_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['right_hip'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    right_hip_linear_displacement_sway: {
        id: 'right_hip_linear_displacement_sway',
        measurementName: 'right_hip_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['right_hip'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    right_hip_linear_displacement_lift: {
        id: 'right_hip_linear_displacement_lift',
        measurementName: 'right_hip_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['right_hip'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    pelvis_linear_displacement_thrust: {
        id: 'pelvis_linear_displacement_thrust',
        measurementName: 'pelvis_linear_displacement',
        axisIndex: 0,
        label: 'Thrust',
        unit: 'm',
        targetPoints: ['pelvis'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: -Math.PI / 2, polar: Math.PI / 2 },
    },
    pelvis_linear_displacement_sway: {
        id: 'pelvis_linear_displacement_sway',
        measurementName: 'pelvis_linear_displacement',
        axisIndex: 1,
        label: 'Sway',
        unit: 'm',
        targetPoints: ['pelvis'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
    pelvis_linear_displacement_lift: {
        id: 'pelvis_linear_displacement_lift',
        measurementName: 'pelvis_linear_displacement',
        axisIndex: 2,
        label: 'Lift',
        unit: 'm',
        targetPoints: ['pelvis'],
        camera: { type: LOCK_TYPE.STATIC, azimuthal: 0, polar: Math.PI / 2 },
    },
};
